import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Injector,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AbstractValueAccessor, MakeProvider } from '../abstract-value-accessor/abstract-value-accessor';
import { Placement } from '@ng-bootstrap/ng-bootstrap';
import { WindowHelperService } from '@anecdotes/utils';

interface HighlightNode {
  type: 'term' | 'text';
  value: string;
}

@Component({
  selector: 'app-text-area',
  templateUrl: './text-area.component.html',
  styleUrls: ['./text-area.component.scss'],
  providers: [MakeProvider(TextAreaComponent)],
  standalone: false,
})
export class TextAreaComponent extends AbstractValueAccessor implements OnInit {
  @ViewChild('textField', { static: true })
  private textFieldElement: ElementRef<HTMLInputElement>;

  constructor(
    private windowHelper: WindowHelperService,
    private elementRef: ElementRef<HTMLElement>,
    private cd: ChangeDetectorRef,
    injector: Injector,
  ) {
    super(injector);
  }

  @HostBinding('class.disabled')
  private get disabled(): boolean {
    return this.isDisabled;
  }

  @HostBinding('class.dirty')
  get dirty(): boolean {
    return this.formControl?.dirty;
  }

  @HostBinding('class.invalid')
  get invalid(): boolean {
    return this.formControl?.invalid;
  }

  @HostBinding('class.has-value')
  get hasValue(): boolean {
    return this.value;
  }

  @HostBinding('class.active')
  isActive: boolean;

  @Input()
  maxLength: number;

  @Input()
  errorTexts: { [key: string]: string | (() => string) };

  @Input()
  label: string;

  @Input()
  labelWithColon = true;

  @Input()
  labelParamsObj: any;

  @Input()
  required: boolean;

  @Input()
  infoTooltip: string;

  @Input()
  infoTooltipPlacement: Placement;

  @Input()
  placeholder: string | TemplateRef<any>;

  @Input()
  placeholderParamsObj: any;

  @Input()
  placeholderIcon: string;

  @Input()
  displayCharactersCounter: boolean;

  @Input()
  validateOnDirty = false;

  @Input()
  index: number;

  @Input()
  removable: boolean;

  @Input()
  readonly: boolean;

  @Input()
  termsToHighlight: string[];

  @Input()
  rows: number;

  @Input()
  minNumberOfRows: number;

  @Input()
  minTextareaHeight: number;

  @Input()
  resizable = true;

  @Input()
  descriptionTooltipText: string;

  @HostBinding('attr.tabIndex')
  @Input()
  tabIndex = '-1';

  @HostBinding('class.autogrow')
  @Input()
  autogrow: boolean;

  @Input()
  displayErrors: boolean;

  @Input()
  isEnteringNewLineDisabled = false;

  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-native
  input = new EventEmitter<InputEvent | Event>();

  @Output()
  valueChanges = new EventEmitter<string>();

  @Output()
  removeControl = new EventEmitter<string>();

  @Output()
  focusOut = new EventEmitter<void>();

  ngOnInit(): void {
    super.ngOnInit();

    if (!this.rows && this.minNumberOfRows && this.autogrow) {
      this.minTextareaHeight ||= this.getTextareaMinHeight();
    }
  }

  focus(): void {
    this.textFieldElement.nativeElement.focus();
  }

  placeholderClick(): void {
    if (!this.isDisabled) {
      this.textFieldElement.nativeElement.focus();
    }
  }

  inputBlur(): void {
    this.markAsDirty();
  }

  inputFocusIn(): void {
    this.isActive = true;
    this.elementRef.nativeElement.dispatchEvent(new FocusEvent('focusin'));
    this.cd.detectChanges();
  }

  inputFocusOut(): void {
    if (this.textFieldElement.nativeElement.value) {
      this.textFieldElement.nativeElement.value = this.textFieldElement.nativeElement.value.trim();
    }

    this.isActive = false;
    this.elementRef.nativeElement.dispatchEvent(new FocusEvent('focusout'));
    this.focusOut.emit();
    this.cd.detectChanges();
  }

  getHighlightedSyntax(): HighlightNode[] {
    if (!this.value || !this.termsToHighlight?.length) {
      return null;
    }

    let text: string = this.value;
    const terms = this.termsToHighlight;
    const termsSet = new Set(terms);

    terms.forEach((term) => {
      text = text.replace(new RegExp(term, 'g'), `%%${term}%%`);
    });

    let hasTerms = false;

    const nodes = text.split('%%').map((textPart) => {
      if (termsSet.has(textPart)) {
        hasTerms = true;
        return { type: 'term', value: textPart };
      }

      return { type: 'text', value: textPart };
    });

    if (hasTerms) {
      return nodes as HighlightNode[];
    }

    return null;
  }

  protected modelChange(text: string): void {
    if (text !== this.value) {
      this.value = text;
      this.valueChanges.emit(text);
    }
  }

  @HostListener('document:keydown.enter', ['$event'])
  private onEnterHandler(event: KeyboardEvent): void {
    if (this.isEnteringNewLineDisabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
  }

  private getTextareaMinHeight(): number {
    const textareaStyles = this.windowHelper.getWindow().getComputedStyle(this.textFieldElement.nativeElement);
    const lineHeight = parseFloat(textareaStyles.lineHeight);
    const paddingTop = parseFloat(textareaStyles.paddingTop);
    const paddingBottom = parseFloat(textareaStyles.paddingBottom);
    const borderTop = parseFloat(textareaStyles.borderTop);
    const borderBottom = parseFloat(textareaStyles.borderBottom);
    const paddingSize = paddingTop + paddingBottom;
    const borderSize = borderTop + borderBottom;
    const minHeight = (this.minNumberOfRows * lineHeight) + paddingSize + borderSize;

    return minHeight;
  }
}
